import { getApiBase, getAuthHeaders } from "./api";

export interface StreamSnapItem {
  foodName: string;
  category: string;
  grams: number;
  confidence: number;
  description: string;
  inferred: boolean;
}

export interface StreamSnapPlateOptions {
  images: string[];
  description?: string;
  onItem: (item: StreamSnapItem, index: number) => void;
  onDone: (items: StreamSnapItem[]) => void;
  onError: (error: string, partialItems: StreamSnapItem[]) => void;
  signal?: AbortSignal;
}

export async function streamSnapPlate(opts: StreamSnapPlateOptions): Promise<void> {
  const { images, description = "", onItem, onDone, onError, signal } = opts;
  const base = getApiBase();
  const authHeaders = await getAuthHeaders();

  let res: Response;
  try {
    res = await fetch(`${base}/neural/snap-plate/stream`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders },
      credentials: "include",
      body: JSON.stringify({ images, description }),
      signal,
    });
  } catch (err: any) {
    if (err?.name === "AbortError") return;
    onError(err?.message ?? "Connection failed", []);
    return;
  }

  if (!res.ok) {
    let msg = `Server error ${res.status}`;
    try {
      const data = await res.json();
      if (data?.error) msg = data.error;
    } catch {}
    onError(msg, []);
    return;
  }

  if (!res.body) {
    onError("No response body — streaming not supported by this client", []);
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  const collectedItems: StreamSnapItem[] = [];
  let sawDoneEvent = false;

  try {
    while (true) {
      const { done, value } = await reader.read();

      if (signal?.aborted) {
        return;
      }

      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() ?? "";

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed.startsWith("data: ")) continue;
        const raw = trimmed.slice(6).trim();
        if (!raw || raw === "[DONE]") continue;

        let event: any;
        try {
          event = JSON.parse(raw);
        } catch {
          continue;
        }

        if (event.type === "item" && event.item) {
          const item = event.item as StreamSnapItem;
          collectedItems.push(item);
          onItem(item, collectedItems.length - 1);
        } else if (event.type === "done") {
          sawDoneEvent = true;
          onDone(collectedItems);
          return;
        } else if (event.type === "error") {
          onError(event.error ?? "Analysis failed", collectedItems);
          return;
        }
      }
    }

    if (!sawDoneEvent && !signal?.aborted) {
      onError("Stream closed unexpectedly", collectedItems);
    }
  } catch (err: any) {
    if (err?.name === "AbortError" || signal?.aborted) {
      return;
    }
    onError(err?.message ?? "Stream read failed", collectedItems);
  } finally {
    reader.releaseLock();
  }
}
